/**
 * ConsultationDetailPage — J7-W3 task 3b/3c (PRD §12.3.10)
 *
 * Read-only view of a persona consultation. Shows context + question +
 * each persona's response side-by-side, plus the synthesis (council mode).
 * Links to the conversation_id when the conversations module is wired.
 */

import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { MessagesSquare, ArrowLeft } from 'lucide-react';
import { t } from '@/lib/i18n';
import { RorosPage } from '@/components/roros/RorosPage';
import { Card, CardContent } from '@/components/ui/Card/Card';
import { Badge } from '@/components/ui/Badge/Badge';
import { Button } from '@/components/ui/Button';
import { LoadingState } from '@/components/ui/LoadingState';
import { EmptyState } from '@/components/ui/EmptyState';
import { useRorosConsultation, useRorosActors } from '@/hooks/roros/actors';
import { formatDate } from '@/lib/localeFormatters';
import type { RorosActorDTO } from '@/types/roros/actors';

export function ConsultationDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const consultationQuery = useRorosConsultation(id ?? '');
  const actorsQuery = useRorosActors();

  if (consultationQuery.isLoading) return <LoadingState />;
  if (consultationQuery.isError || !consultationQuery.data) {
    return (
      <RorosPage title={t('Consultation')}>
        <EmptyState
          icon={<MessagesSquare className="h-6 w-6" aria-hidden />}
          title={t('Consultation not found')}
          description={t('This consultation may have been deleted or is in another organization.')}
          action={{
            label: t('Back to Consultations'),
            onClick: () => navigate('/roros/consultations'),
          }}
        />
      </RorosPage>
    );
  }

  const c = consultationQuery.data;
  const actors = (actorsQuery.data as RorosActorDTO[] | undefined) ?? [];
  const requestedBy = actors.find((a) => a.id === c.requestedByActorId);

  return (
    <RorosPage
      title={c.consultationType === 'council' ? t('Council Consultation') : t('Persona Consultation')}
      icon={<MessagesSquare className="h-5 w-5" aria-hidden />}
      actions={
        <Button variant="outline" onClick={() => navigate('/roros/consultations')} leftIcon={<ArrowLeft className="h-4 w-4" />}>
          {t('Back')}
        </Button>
      }
    >
      <div className="mt-4 space-y-4">
        {/* Metadata */}
        <Card>
          <CardContent className="p-6 space-y-3">
            <div className="flex items-center justify-between">
              <Badge variant="outline">{c.consultationType === 'council' ? t('Council') : t('Single')}</Badge>
              <span className="text-xs text-muted-foreground">
                {c.createdAt ? formatDate(new Date(c.createdAt)) : '—'}
              </span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
              <div>
                <p className="text-xs text-muted-foreground">{t('Requested by')}</p>
                <p className="font-medium">{requestedBy?.name ?? c.requestedByActorId.slice(0, 8)}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">{t('Reference')}</p>
                <p className="font-medium">
                  {c.referenceEntityType
                    ? `${c.referenceEntityType}:${(c.referenceEntityId ?? '').slice(0, 8)}`
                    : t('—')}
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">{t('Model tier')}</p>
                <p className="font-medium capitalize">{c.modelTier}</p>
              </div>
              {c.conversationId && (
                <div>
                  <p className="text-xs text-muted-foreground">{t('Conversation')}</p>
                  <p className="font-medium font-mono text-xs">{c.conversationId.slice(0, 8)}</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Context + Question */}
        <Card>
          <CardContent className="p-6 space-y-4">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">
                {t('Context')}
              </p>
              <p className="text-sm whitespace-pre-wrap" data-testid="consultation-context">
                {c.context}
              </p>
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">
                {t('Question')}
              </p>
              <p className="text-sm whitespace-pre-wrap" data-testid="consultation-question">
                {c.question}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Per-persona responses */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="consultation-responses">
          {(c.responses ?? []).map((r, idx) => (
            <Card key={`${r.personaId}-${idx}`}>
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-sm font-semibold text-foreground">{r.personaName}</p>
                  <span className="text-xs text-muted-foreground">
                    {r.generatedAt ? formatDate(new Date(r.generatedAt)) : '—'}
                  </span>
                </div>
                <p className="text-sm whitespace-pre-wrap text-foreground/90">{r.response}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Synthesis (council only) */}
        {c.synthesis && (
          <Card>
            <CardContent className="p-6">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
                {t('Council synthesis')}
              </p>
              <p className="text-sm whitespace-pre-wrap" data-testid="consultation-synthesis">
                {c.synthesis}
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </RorosPage>
  );
}
